/**
 * WHEEL-CONFIG.JS - Configuracion Centralizada de la Ruleta
 *
 * ARQUITECTURA:
 * - MATH_CONSTANTS: Constantes matematicas inmutables
 * - WHEEL_CONFIG: Toda la configuracion de la rueda (modificable, vendra del servidor)
 *
 * REQUISITOS FUTUROS:
 * - Cantidad de numeros variable (12, 18, 37)
 * - Orden de numeros configurable
 * - Cantidad de ceros variable
 * - Bloques A-F opcionales
 * - Assets dinamicos desde BD
 */

// Este archivo NO importa nada de roulette-numbers-order-config.js. El orden
// de la rueda, el colorMap y los bloques dependen del tipo de ruleta activo;
// los consume wheel-of-fortune.js directo desde getActiveType(). Exportarlos
// acá como snapshot del import (european fijo) mentía al cambiar de tipo.

// =============================================================================
// CONSTANTES MATEMATICAS (realmente inmutables)
// =============================================================================

export const MATH_CONSTANTS = {
    /** Factor de conversion de grados a radianes */
    TO_RADIANS: Math.PI / 180,
};

// =============================================================================
// CONFIGURACION DE LA RUEDA (todo modificable, vendra del servidor)
// =============================================================================

export const WHEEL_CONFIG = {
    // NOTA: la definición de QUÉ ruleta es (orden, colorMap, ceros, bloques)
    // NO vive acá. Es función del tipo de rueda y la sirve la factory
    // `crearConfiguracionDeRuedaParaTipo` (rueda-config-factory.js). La rama
    // `wheel:` que había acá era declarativa y no la consumía nadie.

    // ---------------------------------------------------------------------------
    // GEOMETRIA DE LA RUEDA
    // Todos los valores son relativos al radio (0-1)
    // ---------------------------------------------------------------------------
    geometry: {
        /** Radio exterior de la rueda (borde de numeros) */
        rOut: 0.98,

        /** Radio interior (centro de la rueda) */
        rIn: 0.35,

        /** Radio medio (limite bloques/numeros). Si no hay bloques, usar mismo que rIn */
        rMid: 0.6,

        /** Posicion del texto de numeros dentro de la franja (0=en rMid, 1=en rOut) */
        rNumberPos: 0.78,

        /** Altura extra del canvas para el flapper (relativo al size) */
        canvasExtraHeight: 0.063,
    },

    // ---------------------------------------------------------------------------
    // RENDERIZADO GENERAL
    // ---------------------------------------------------------------------------
    rendering: {
        /** Grosor del borde exterior en pixeles */
        outerBorderWidth: 8,

        // innerBorderWidth eliminado de aqui: el grosor del borde interno
        // ahora vive en theme.barWidth (default) con override por tipo en
        // cfg.geometry.innerBorderWidth (ver roulette-numbers-order-config).
        // Aqui solo causaba conflicto de nombres con un valor huerfano (6).

        /** Grosor de las lineas divisoras entre numeros */
        dividerWidth: 2,

        /** Tamano de los numeros (0-1, relativo al size) */
        numberSize: 0.045,

        /** Tamano del label especial (LOTO) (0-1, relativo al size) */
        labelSize: 0.018,

        /** Tamano de las letras de bloque A,B,C... (relativo al size) */
        blockLabelSize: 0.06,

        /** Radio de los pines/bolardos (0-1, relativo al size) */
        pinRadius: 0.006,

        /** Familia de fuente para los numeros */
        fontFamily: 'RouletteNumbers, Arial, sans-serif',

        /** Activar sombras en el renderizado */
        useShadows: true,

        /** Blur de sombras (para numeros, etc.) */
        shadowBlur: 15,

        /** Color de las lineas divisoras entre numeros */
        dividerColor: '#f4e582',
    },

    // ---------------------------------------------------------------------------
    // COLORES BASE NEUTROS (no semantica de "rojo/negro" — color del tema)
    // El texto de los numeros se pinta neutro blanco; los gradientes del sector
    // salen del tema (theme.paletas[N].gradientes). El "rojo/negro/cero" YA NO
    // vive aca: la receta declara `sector.paleta` y el tema decide el color.
    // ---------------------------------------------------------------------------
    colors: {
        text: '#ffffff', // Texto blanco neutro sobre numeros (legibilidad)
    },

    // ---------------------------------------------------------------------------
    // FLAPPER EXTERIOR (rojo, superior)
    // ---------------------------------------------------------------------------
    flapperOuter: {
        /** Longitud del flapper (relativo al radio) */
        length: 0.1,

        /** Offset vertical desde el borde superior (-=fuera, +=dentro) */
        offsetY: -15,

        /** Tamano del housing/circulo (relativo al size) */
        housingSize: 0.015,

        /** Tamano del puntero/flecha (0-1, relativo al size) */
        pointerSize: 0.025,

        /** Color del flapper */
        color: '#cc1a1a',

        /** Color del housing */
        housingColor: '#1a1a1a',
    },

    // ---------------------------------------------------------------------------
    // FLAPPER INTERIOR (dorado, central)
    // ---------------------------------------------------------------------------
    flapperInner: {
        /** Ancho del flapper (relativo a rIn) */
        width: 0.06,

        /** Radio del housing (relativo a rIn) */
        housing: 0.04,

        /** Altura del flapper (relativo a rIn) */
        height: 0.25,
    },

    // ---------------------------------------------------------------------------
    // SPIN / FISICA
    // ---------------------------------------------------------------------------
    spin: {
        /** Duracion total del spin en milisegundos */
        duration: 8000,

        /** Velocidad de crucero en RPM (rotaciones por minuto) */
        cruiseRPM: 45,

        /** Numero de "quesitos" en el patron FM (Flapper/Motor) */
        fmQuesitos: 10,

        /**
         * Porcentajes de cada fase del spin (deben sumar 1.0)
         * accel: Fase de aceleracion
         * cruise: Fase de velocidad constante
         * decel: Fase de desaceleracion
         */
        // Perfil de agonía (decisión de producto): arranque corto, crucero
        // mínimo y 85% del giro en desaceleración — la rueda "muere" lento
        // frente al jugador para maximizar el suspenso del resultado.
        phases: {
            accel: 0.1, // 10% aceleracion
            cruise: 0.05, // 5% crucero
            decel: 0.85, // 85% desaceleracion (agonia)
        },
    },

    // ---------------------------------------------------------------------------
    // AGONIA (desaceleracion final psicologica)
    // ---------------------------------------------------------------------------
    agony: {
        enabled: true,
        /** Los ultimos N numeros seran los "agonicos" */
        lastClicks: 3,
        /** Cuanto se estira el tiempo en estos ultimos clics (mas alto = mas lento) */
        tensionMultiplier: 2.5,
    },

    // ---------------------------------------------------------------------------
    // IDLE (comportamiento en espera / respiracion)
    // ---------------------------------------------------------------------------
    idle: {
        /**
         * Cadencia del "bum" del idle: milisegundos que tarda el flapper en
         * cruzar UN quesito. El idle deriva su velocidad angular de acá
         * (velocidad = gradosPorQuesito / esta cadencia), así el bum suena al
         * mismo ritmo en ruedas de 10, 12 o 37 sectores — antes la velocidad
         * era fija y el bum se espaciaba en ruedas de pocos quesitos.
         */
        msPorQuesito: 4000,

        /** Amplitud de la respiracion (escala) */
        breathAmplitude: 0.025,

        /** Velocidad de la respiracion */
        breathSpeed: 0.002,
    },

    // ---------------------------------------------------------------------------
    // OVERLAY (rayos hot/cold sobre numeros)
    // ---------------------------------------------------------------------------
    overlay: {
        hot: {
            gradient: [
                [0, 'rgba(255, 72, 0, 1)'],
                [0.5, 'rgba(255, 115, 0, 1)'],
                [0.82, 'rgba(255, 229, 174, 1)'],
                [0.9, 'rgba(255, 221, 0, 1)'],
                [1, 'rgba(255, 200, 0, 1)'],
            ],
            stroke: { color: 'rgba(255, 200, 0, 1)', width: 2, shadowBlur: 2 },
        },
        cold: {
            gradient: [
                [0, 'rgba(132, 180, 252, 1)'],
                [0.3, 'rgba(106, 197, 250, 1)'],
                [0.72, 'rgba(174, 230, 255, 1)'],
                [0.85, 'rgba(103, 203, 249, 1)'],
                [0.9, 'rgba(158, 209, 249, 1)'],
            ],
            stroke: { color: 'rgba(0, 208, 255, 1)', width: 1, shadowBlur: 2 },
        },
        neutral: {
            gradient: [
                [0, 'rgba(120, 120, 120, 0.3)'],
                [1, 'rgba(202, 202, 202, 0.7)'],
            ],
            stroke: { color: 'rgba(150, 150, 150, 0.5)', width: 1, shadowBlur: 0 },
        },
        geometry: {
            innerMargin: 1.012, // Multiplicador de rMid (pegado a los bloques)
            outerMargin: 0.65, // Máximo % donde llega el rayo del borde
            minLengthRatio: 0.36, // Longitud minima del rayo mas corto (0-1) - continuo
            anglePadding: 0.7, // Padding angular en grados
        },
        animation: {
            pulseSpeed: 0.09, // Más rápido = más urgencia emocional
            alphaBase: 1,
            alphaRange: 0.25, // Mayor variabilidad = más atención (Skinner)
        },
        effects: {
            blendMode: 'source-over',
            fillShadowBlur: 8, // Glow pronunciado para premios (sensory reinforcement)
        },
    },

    // ---------------------------------------------------------------------------
    // EFECTOS VISUALES (ripples, ondas, bursts)
    // ---------------------------------------------------------------------------
    effects: {
        /** Grosor del nucleo de la onda (relativo al size) */
        strokeWidth: 0.015,

        /** Grosor del halo exterior (relativo al size) */
        haloWidth: 0.04,

        /** Grosor de la onda interna (relativo al size) */
        innerWaveWidth: 0.02,

        /** Opacidad del halo */
        haloOpacity: 0.3,

        /** Opacidad de la onda interna */
        innerWaveOpacity: 0.25,

        /** Presets de colores */
        presets: {
            yellow: { color: '#ffeb3b', duration: 2800 },
            gold: { color: '#ffd700', duration: 3000 },
            orange: { color: '#ff9800', duration: 2600 },
            burst: { color: '#ffeb3b', count: 4, delay: 500 },
        },
    },

    // ---------------------------------------------------------------------------
    // ASSETS (dinamicos, vendran del servidor)
    // Pueden ser: URL, path local, o base64. null = no mostrar
    // ---------------------------------------------------------------------------
    assets: {
        logoBeneficencia: 'assets/logo-beneficencia.png',
        logoLoteria: 'assets/logo-loteria.svg',
    },

    // ---------------------------------------------------------------------------
    // FEATURES — interruptores de efectos opcionales del front-tv. Default
    // todo en true (comportamiento actual). Pensados para que un día el admin
    // los exponga por juego/TV (ej. apagar efectos en hardware muy débil): el
    // front YA los respeta, ese día solo se agrega el toggle en el admin.
    // El consumidor debe tratar `undefined` como true (config vieja sin la
    // clave no debe apagar nada).
    // ---------------------------------------------------------------------------
    features: {
        /** Overlay full-screen "SORTEO Nº" antes del giro. */
        overlayNumeroSorteo: true,
        /** Destello (zoom) del número en el histórico y en hot/cold. */
        destelloNumeroSorteo: true,
        /** Órbita 360 de los paneles laterales al cerrar apuestas. */
        orbitaPaneles: true,
        /** Panel de estadísticas (canvas). false → no se monta el canvas. */
        statsPanel: true,
    },
};

// =============================================================================
// HELPER: Deep Merge para config del servidor
// =============================================================================

/**
 * Merge profundo de configuracion
 * @param {object} target - Config base
 * @param {object} source - Overrides del servidor
 * @returns {object} Config mergeada
 */
function deepMerge(target, source) {
    if (!source) return target;

    const result = { ...target };

    for (const key of Object.keys(source)) {
        if (
            source[key] &&
            typeof source[key] === 'object' &&
            !Array.isArray(source[key]) &&
            target[key] &&
            typeof target[key] === 'object' &&
            !Array.isArray(target[key])
        ) {
            result[key] = deepMerge(target[key], source[key]);
        } else {
            result[key] = source[key];
        }
    }

    return result;
}

/**
 * Merge config del servidor con defaults
 * @param {object} serverConfig - Config recibida del servidor
 * @returns {object} Config final con defaults aplicados
 */
export function mergeWheelConfig(serverConfig) {
    return deepMerge(WHEEL_CONFIG, serverConfig);
}

// =============================================================================
// COMPATIBILIDAD HACIA ATRAS
// Exports que mantienen la API anterior para no romper imports existentes
// =============================================================================

/**
 * @deprecated Usar WHEEL_CONFIG.geometry y MATH_CONSTANTS
 *
 * Ya NO incluye ORDEN_RUEDA_ACTIVA / COLOR_MAP / BLOCK_MAPPING: eran un
 * snapshot del tipo de ruleta al import (european) que no reflejaba
 * setActiveType. wheel-of-fortune.js los sincroniza desde getActiveType()
 * en su constructor (_sincronizarConstantsConTipoActivo).
 */
export const WHEEL_CONSTANTS = {
    TO_RADIANS: MATH_CONSTANTS.TO_RADIANS,
    R_OUT: WHEEL_CONFIG.geometry.rOut,
    R_IN: WHEEL_CONFIG.geometry.rIn,
    R_MID: WHEEL_CONFIG.geometry.rMid,
    R_NUMBER_POS: WHEEL_CONFIG.geometry.rNumberPos,
    CANVAS_EXTRA_HEIGHT: WHEEL_CONFIG.geometry.canvasExtraHeight,
};

/** Colores neutros — solo `TEXT` (blanco para legibilidad sobre numeros). */
export const BASE_COLORS = {
    TEXT: WHEEL_CONFIG.colors.text,
};

/** @deprecated Usar WHEEL_CONFIG.spin y WHEEL_CONFIG.agony */
export const DEFAULT_CONFIG = {
    size: 800,
    duration: WHEEL_CONFIG.spin.duration,
    fmQuesitos: WHEEL_CONFIG.spin.fmQuesitos,
    cruiseRPM: WHEEL_CONFIG.spin.cruiseRPM,
    phases: { ...WHEEL_CONFIG.spin.phases },
    agony: { ...WHEEL_CONFIG.agony },
};

/** @deprecated Usar WHEEL_CONFIG.rendering, flapperOuter, flapperInner */
export const DEFAULT_RENDER_OPTS = {
    outerBorderWidth: WHEEL_CONFIG.rendering.outerBorderWidth,
    dividerWidth: WHEEL_CONFIG.rendering.dividerWidth,
    numberSize: WHEEL_CONFIG.rendering.numberSize,
    labelSize: WHEEL_CONFIG.rendering.labelSize,
    pointerSize: WHEEL_CONFIG.flapperOuter.pointerSize,
    pinRadius: WHEEL_CONFIG.rendering.pinRadius,
    fontFamily: WHEEL_CONFIG.rendering.fontFamily,
    blockLabelSize: WHEEL_CONFIG.rendering.blockLabelSize,
    flapperLength: WHEEL_CONFIG.flapperOuter.length,
    flapperOffsetY: WHEEL_CONFIG.flapperOuter.offsetY,
    flapperHousingSize: WHEEL_CONFIG.flapperOuter.housingSize,
    innerFlapperWidth: WHEEL_CONFIG.flapperInner.width,
    innerFlapperHousing: WHEEL_CONFIG.flapperInner.housing,
    innerFlapperHeight: WHEEL_CONFIG.flapperInner.height,
    useShadows: WHEEL_CONFIG.rendering.useShadows,
};

/** @deprecated Usar WHEEL_CONFIG.effects.presets */
export const EFFECT_PRESETS = {
    yellow: { ...WHEEL_CONFIG.effects.presets.yellow, type: 'single' },
    gold: { ...WHEEL_CONFIG.effects.presets.gold, type: 'single' },
    orange: { ...WHEEL_CONFIG.effects.presets.orange, type: 'single' },
    burst: { ...WHEEL_CONFIG.effects.presets.burst, type: 'burst' },
};

// =============================================================================
// NAMESPACE EXPORT
// =============================================================================

export const WheelConfig = {
    // Nueva API
    MATH_CONSTANTS,
    WHEEL_CONFIG,
    mergeWheelConfig,

    // Compatibilidad
    WHEEL_CONSTANTS,
    BASE_COLORS,
    DEFAULT_CONFIG,
    DEFAULT_RENDER_OPTS,
    EFFECT_PRESETS,
};

export default WheelConfig;

// =============================================================================
// BACKWARD COMPATIBILITY - CommonJS
// =============================================================================

if (typeof module !== 'undefined' && module.exports) {
    module.exports = WheelConfig;
}
